import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const EVENT_FIELDS = ['Name', 'workerReference_c', 'jobReference_c', 'eventType_c', 'consentRecorded_c', 'capturedAt_c'];

export const route = { path: '/worker-tracking', layout: 'owner', access: "profile === 'Agent_c'" };
export const nav = { icon: 'MapPin', label: 'Worker tracking', section: 'Operations', profiles: ['Agent_c'], order: 25 };

export default function WorkerTracking() {
  const { user } = useAuth();
  const [workerId, setWorkerId] = useState('');
  const [jobId, setJobId] = useState('');
  const [eventType, setEventType] = useState('Arrived');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: workers } = useFetch(() => sdk.table('workers_c').select(['Name', 'phone_c', 'status_c']).orderByDesc('CreatedOn').fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load workers');
    return r.data;
  }), []);
  const { data: jobs } = useFetch(() => sdk.table('jobs_c').select(['Name', 'status_c', 'scheduledAt_c']).orderByDesc('CreatedOn').fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load jobs');
    return r.data;
  }), []);
  const { data: events, loading, error, run } = useFetch(() => sdk.table('tracking_events_c').select(EVENT_FIELDS).orderByDesc('CreatedOn').limit(50, 0).fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load visit events');
    return r.data;
  }), []);

  async function recordEvent(event) {
    event.preventDefault();
    if (!workerId || !jobId) { toast.error('Select a worker and a service job.'); return; }
    if (!consent) { toast.error('Confirm documented worker consent before recording a visit event.'); return; }
    const job = (jobs ?? []).find((row) => String(row.Id) === String(jobId));
    if (!job || ['Completed', 'Cancelled'].includes(job.status_c)) { toast.error('Visit events can only be recorded for an active job.'); return; }
    setSaving(true);
    try {
      const result = await sdk.table('tracking_events_c').create({
        Name: eventType + ' · ' + job.Name,
        workerReference_c: String(workerId),
        jobReference_c: String(jobId),
        eventType_c: eventType,
        consentRecorded_c: true,
        capturedAt_c: new Date().toISOString()
      });
      if (result.messages?.length) toast.error(result.messages[0]);
      const saved = result.data?.[0];
      if (!saved) return;
      try {
        await sdk.table('audit_logs_c').create({
          Name: 'Visit event recorded: ' + eventType,
          actorUserId_c: user?.userId || 'unknown',
          actorProfile_c: 'Agent_c',
          action_c: 'WORKER_VISIT_EVENT_RECORDED',
          entityType_c: 'tracking_events_c',
          entityId_c: String(saved.Id),
          afterJson_c: JSON.stringify({ workerReference_c: String(workerId), jobReference_c: String(jobId), eventType_c: eventType }),
          source_c: 'Agent portal',
          createdAt_c: new Date().toISOString()
        });
      } catch (auditError) {
        toast.warning('Visit event saved, but its audit event failed.');
      }
      toast.success('Visit event saved.');
      setConsent(false);
      run();
    } catch (error) {
      toast.error(error.message || 'Could not record visit event.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">Field operations</p>
        <h1 className="mt-2 font-heading text-3xl font-bold">Worker tracking</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Record consented job milestones and keep a traceable visit history.</p>
      </header>
      <section className="glass-panel rounded-[24px] p-5 sm:p-7">
        <h2 className="font-heading text-xl font-bold">Record a visit milestone</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">This logs a milestone only. It does not collect GPS or track a worker continuously. Location capture must be separately enabled in a worker-facing app with explicit consent and active-job limits.</p>
        <form onSubmit={recordEvent} className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="grid gap-2 text-sm font-medium">Worker
            <select required value={workerId} onChange={(e) => setWorkerId(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3">
              <option value="">Choose worker</option>
              {(workers ?? []).map((worker) => <option key={worker.Id} value={worker.Id}>{worker.Name} · {worker.status_c || 'Status pending'}</option>)}
            </select>
          </label>
          <label className="grid gap-2 text-sm font-medium">Active service job
            <select required value={jobId} onChange={(e) => setJobId(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3">
              <option value="">Choose job</option>
              {(jobs ?? []).filter((job) => !['Completed', 'Cancelled'].includes(job.status_c)).map((job) => <option key={job.Id} value={job.Id}>{job.Name} · {job.status_c}</option>)}
            </select>
          </label>
          <label className="grid gap-2 text-sm font-medium sm:col-span-2">Milestone
            <select value={eventType} onChange={(e) => setEventType(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3">
              <option>Assigned</option><option>En route</option><option>Arrived</option><option>Started</option><option>Completed</option><option>Location consent withdrawn</option>
            </select>
          </label>
          <label className="flex items-start gap-3 rounded-xl border border-border p-3 text-sm leading-5 sm:col-span-2">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
            <span>I confirm that the worker has been informed and has consented to this active-job visit event. I am not recording a location outside the assigned job.</span>
          </label>
          <div className="sm:col-span-2"><Button type="submit" disabled={saving}>{saving ? 'Saving event…' : 'Save visit event'}</Button></div>
        </form>
      </section>
      <section className="glass-panel rounded-[24px] p-5 sm:p-7">
        <div className="flex items-center justify-between gap-3"><h2 className="font-heading text-xl font-bold">Recent visit events</h2><Button variant="outline" onClick={run} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Button></div>
        {error && <p className="mt-4 text-sm text-destructive">Could not load events: {error.message}</p>}
        <div className="mt-4 space-y-3">
          {(events ?? []).map((row) => <article key={row.Id} className="rounded-xl border border-border p-4"><div className="flex flex-wrap items-center justify-between gap-2"><p className="font-semibold">{row.eventType_c}</p><span className="rounded-full bg-muted px-2.5 py-1 text-xs">{row.consentRecorded_c ? 'Consent recorded' : 'Consent missing'}</span></div><p className="mt-1 text-sm text-muted-foreground">Job {row.jobReference_c} · Worker {row.workerReference_c}</p><p className="mt-1 text-xs text-muted-foreground">{row.capturedAt_c ? new Date(row.capturedAt_c).toLocaleString('en-IN') : 'Time unavailable'}</p></article>)}
          {!loading && !(events ?? []).length && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No visit events recorded yet.</p>}
        </div>
      </section>
      <Button variant="outline" asChild><Link to="/jobs">Open booking queue</Link></Button>
    </div>
  );
}
